import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { CustomLinkRender, CustomTagRender, DOMAIN_ID, getUserName, ORG_ID, isEmpty } from "../../../utils/helpers";
import { useDebounce } from "../../../utils/hooks";
import { Badge, Card, Drawer, message, Tag, Tooltip } from "antd";
import DitPolicyViewer from "./ditPolicyViewer";
import { dbPolicyFlowLabel, normalizeDbPolicyFlow } from "../db-policies/dbPolicyModel";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContentLayout, PageSection } from "../../../layout/applayout";
import { getPolicyList } from "../../../service/appService";
import { FilterComponents } from "../../../components/ui/common";
import DataGridTable from "../../../components/ui/datatable/DataGridTable";
import { UserTimezoneRender } from "../../../components/ui/datatable/DataTableContent";

function PolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [policySearch, setPolicySearch] = useState("");
  const [selectedPolicyType, setSelectedPolicyType] = useState("AD");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedGeneratedBy, setSelectedGeneratedBy] = useState(null);
  const [policyData, setPolicyData] = useState([]);
  const [viewPolicyDrawer, setViewPolicyDrawer] = useState(false);
  const [policyJsonData, setPolicyJsonData] = useState()
  //pagination
  const PAGESIZE = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    pageSize: PAGESIZE,
  });

  // UI configuration
  /// Filter
  const debouncedPolicySearch = useDebounce(policySearch, 500);
  const [appliedFilters, setAppliedFilters] = useState({ policyType: "AD", iamUser: getUserName() });
  const statusOptions = useMemo(
    () => [
      { name: "Approved", value: "Approved" },
      { name: "Pending", value: "Pending" },
    ],
    [],
  );

  const policyTypesOptions = [
    { name: "Active Directory", value: "AD" },
    { name: "AD Groups", value: "GroupAD" },
    { name: "Service Account", value: "ServiceAccount" },
    { name: "Endpoints", value: "endpoints" },
    { name: "RADIUS", value: "radius" },
    { name: "Database", value: "database" },
  ];

  const generatedByOptions = useMemo(
    () => [
      { value: "Auto", name: "Auto" },
      { value: "AI", name: "AI" },
      { value: "Manual", name: "Manual" },
    ],
    [],
  );

  const [filterstate, setFilterState] = useState([
    {
      isAutocomplete: true,
      inputValue: "",
      inputName: "search",
      labelName: "Search by policy name",
      handleSearch: (value) => setPolicySearch(value),
      results: [],
      handleOnChange: null,
      handleOnClear: null,
    },
    {
      isSelect: true,
      inputValue: selectedPolicyType,
      inputName: "policyType",
      labelName: "Policy Type",
      handleOnChange: (value, name) => {
        applyFilter("policyType", value);
        setSelectedPolicyType(value);
      },
      options: policyTypesOptions,
    },
    {
      isSelect: true,
      inputValue: selectedStatus,
      inputName: "status",
      labelName: "Status",
      handleOnChange: (value, name) => {
        applyFilter("status", value);
        setSelectedStatus(value);
      },
      options: statusOptions,
    },
    {
      isSelect: true,
      inputValue: selectedGeneratedBy,
      inputName: "generatedBy",
      labelName: "Generated by",
      handleOnChange: (value, name) => {
        applyFilter("generatedBy", value);
        setSelectedGeneratedBy(value);
      },
      options: generatedByOptions,
    },
  ]);
  /// DataTable
  const tableStructure = useMemo(() => {
    const ADColumnsData = [
      {
        headerName: "AD Users",
        field: "adUsers",
        flex: 1.5,
        renderCell: (params) => {
          const adUsers = params.row.policyJson?.ad?.users || [];
          if (!adUsers.length) return <span>N/A</span>;
          return adUsers.map((user, index) => <Tag key={index}>{user}</Tag>);
        },
      },
    ];

    // RADIUS
    const RadiusColumnsData = [
      {
        headerName: "RADIUS Endpoints",
        field: "radiusEndpoints",
        flex: 1,
        renderCell: (params) => {
          const endpoints = params.row.policyJson?.networks?.endpoints || [];
          if (!endpoints.length) return <span>N/A</span>;
          return endpoints.map((ep, idx) => <Tag key={idx}>{ep}</Tag>);
        },
      },
    ];

    // Endpoints
    const EndpointsColumnsData = [
      {
        headerName: "Endpoints Groups",
        field: "endpointGroups",
        flex: 2,
        renderCell: (params) => {
          const groupsObj = params.row.policyJson?.endpoints?.groups || {};
          const groupKeys = Object.keys(groupsObj);
          if (!groupKeys.length) return <span>N/A</span>;
          return groupKeys.map((group, index) => <Tag key={index}>{group}</Tag>);
        },
      },
      {
        headerName: "Endpoints",
        field: "endpoints",
        flex: 2,
        renderCell: (params) => {
          const groupsObj = params.row.policyJson?.endpoints?.groups || {};
          const endpoints = Object.values(groupsObj).flat();
          if (!endpoints.length) return <span>N/A</span>;
          return endpoints.map((endpoint, index) => (
            <Tag color="blue" key={index}>
              {endpoint}
            </Tag>
          ));
        },
      },
      {
        headerName: "Endpoints Users",
        field: "endpointUsers",
        flex: 2,
        renderCell: (params) => {
          const endpointUsers = params.row.policyJson?.endpoints?.users || [];
          if (!endpointUsers.length) return <span>N/A</span>;
          return endpointUsers.map((u, idx) => <Tag key={idx}>{u}</Tag>);
        },
      },
    ];

    // ServiceAccount
    const ServiceColumnsData = [
      {
        headerName: "Source Endpoint",
        field: "serviceEndpoints",
        flex: 2,
        renderCell: (params) => {
          const source = params.row.policyJson?.serviceaccount?.source_endpoint || [];
          if (!source.length) return <span>N/A</span>;
          return source.map((s, idx) => <Tag key={idx}>{s}</Tag>);
        },
      },
      {
        headerName: "Destination Endpoint",
        field: "destinationEndpoints",
        flex: 2,
        renderCell: (params) => {
          const dest = params.row.policyJson?.serviceaccount?.destination_endpoint || [];
          if (!dest.length) return <span>N/A</span>;
          return dest.map((d, idx) => <Tag key={idx}>{d}</Tag>);
        },
      },
      {
        headerName: "Service Account Users",
        field: "serviceAccountUsers",
        flex: 2,
        renderCell: (params) => {
          const svcUsers = params.row.policyJson?.serviceaccount?.users || [];
          if (!svcUsers.length) return <span>N/A</span>;
          return svcUsers.map((u, idx) => <Tag key={idx}>{u}</Tag>);
        },
      },
    ];

    // AD Groups
    const GroupADColumnsData = [
      {
        headerName: "AD Group(s)",
        field: "groupADGroups",
        flex: 2,
        renderCell: (params) => {
          const adGroups = params.row.policyJson?.infra?.ad?.ad_groups || [];
          if (!adGroups.length) {
            return <span>N/A</span>;
          }
          return adGroups.map((grp, idx) => <CustomTagRender value={grp} key={`adgroup-${idx}`} />);
        },
      },
    ];

    // Database
    const DATABASE_FLOW_TAG_COLOR = {
      mfa_required: "orange",
      allow: "green",
      block: "red",
    };
    const DatabaseColumnsData = [
      {
        headerName: "Action",
        field: "policyFlow",
        flex: 1,
        renderCell: (params) => {
          // Blank reads as Require MFA — that's what the backend stores for a
          // policy saved before the field existed.
          const flow = normalizeDbPolicyFlow(params.row.policyJson?.policyFlow);
          return (
            <CustomTagRender
              value={dbPolicyFlowLabel(params.row.policyJson?.policyFlow)}
              color={DATABASE_FLOW_TAG_COLOR[flow] || "default"}
            />
          );
        },
      },
      {
        headerName: "Database Name",
        field: "databaseName",
        flex: 1.5,
        renderCell: (params) => {
          const dbName = params.row.policyJson?.database?.database_name;
          return dbName ? <span>{dbName}</span> : <span>N/A</span>;
        },
      },
      {
        headerName: "Tables",
        field: "databaseTables",
        flex: 2,
        renderCell: (params) => {
          const tables = params.row.policyJson?.database?.tables || [];
          if (!tables.length) return <span>N/A</span>;
          return tables.map((value, idx) => <CustomTagRender value={value} key={`table-${idx}`} />);
        },
      },
      {
        headerName: "Masked fields",
        field: "fieldMasking",
        flex: 2,
        renderCell: (params) => {
          const fieldMasking = params.row.policyJson?.database?.field_masking || {};
          const tableKeys = Object.keys(fieldMasking);
          if (!tableKeys.length) {
            return <span>N/A</span>;
          }
          return tableKeys.map((tableKey, idx) => {
            const fields = fieldMasking[tableKey];
            return <CustomTagRender value={fields.join(", ")} key={`maskedFields-${idx}`} />;
          });
        },
      },
      {
        headerName: "Database User",
        field: "dbUser",
        flex: 1,
        renderCell: (params) => {
          const dbUser = params.row.policyJson?.database?.user;
          if (!dbUser) return <span>N/A</span>;
          return <CustomTagRender value={dbUser} />;
        },
      },
      {
        headerName: "Privilege",
        field: "privilege",
        flex: 1,
        renderCell: (params) => {
          const privilege = params.row.policyJson?.database?.privilege || [];
          if (!privilege.length) return <span>N/A</span>;
          return privilege.map((p, idx) => <CustomTagRender key={`previlege-${idx}`} value={p} color="blue" />);
        },
      },
    ];
    const baseColumns = [
      {
        key: "policyName",
        field: "policyName",
        headerName: "Policy Name",
        flex: 1.5,
        renderCell: (val) => {
          const policyName = val.row.policyName;
          if (!policyName) return <span className="no-data-value">N/A</span>;
          return (
            <CustomLinkRender
              value={policyName}
              handleOnclick={() => {
                console.log(val);
                setViewPolicyDrawer(true);
                setPolicyJsonData(val?.row?.policyJson);
              }}
            />
          );
        },
      },
      {
        key: "policyType",
        field: "policyType",
        headerName: "Type",
        flex: 0.6,
        renderCell: (val) => {
          const type = val?.row?.policyType;
          if (!type) return <Tag>N/A</Tag>;
          let displayType = type;
          if (type === "radius") displayType = "RADIUS";
          else if (type === "GroupAD") displayType = "AD Groups";
          else if (type === "ServiceAccount") displayType = "Service Account";
          else if (type === "database") displayType = "Database";
          return <Tag>{displayType}</Tag>;
        },
      },
      {
        key: "iamUsers",
        field: "iamUsers",
        headerName: "IAM Users",
        renderCell: (params) => {
          const iamUsers = params.row.policyJson?.permissions?.iam_users || [];
          if (!iamUsers.length) {
            return <span className="no-data-value">N/A</span>;
          }
          return iamUsers.map((user, index) => <CustomTagRender key={`iamuser-${index}`} value={user} />);
        },
      },
      // Conditionally add columns based on policyType
      ...(selectedPolicyType === "AD" ? ADColumnsData : selectedPolicyType === "GroupAD" ? GroupADColumnsData : selectedPolicyType === "radius" ? RadiusColumnsData : selectedPolicyType === "ServiceAccount" ? ServiceColumnsData : selectedPolicyType === "endpoints" ? EndpointsColumnsData : selectedPolicyType === "database" ? DatabaseColumnsData : []),
      {
        headerName: "Policy Action",
        field: "allowed",
        renderCell: (params) => {
          const allowed = params.row.policyJson?.permissions?.allowed;
          return <CustomTagRender value={allowed ? "Allowed" : "Not Allowed"} color={allowed ? "green" : "red"} />;
        },
      },
      {
        headerName: (
          <div>
            <span style={{ marginRight: ".5rem" }}>Status</span>
            <Tooltip title="Status of the policy can be pending, Active, Inactive, or Deprecated">
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        ),
        field: "status",
        key: "status",
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        renderCell: (val) => <UserTimezoneRender rowValue={val?.row} keyValue="updatedAt" />,
      },
    ];

    return baseColumns;
  }, [selectedPolicyType]);

  // API Callbacks
  const handlePolicyListSuccess = useCallback((response) => {
    const fetchedPolicies = response?.data || [];
    const total = response?.total_pages || 0;
    setPolicyData(fetchedPolicies);
    setTotalCount(total);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);

  const handlePolicyListFailure = useCallback(
    (response) => {
      messageApi.open({
        type: "error",
        content: "Failed to load policies",
      });
      setIsLoading(false);
    },
    [messageApi],
  );

  // API Calls
  const fetchPolicyList = useCallback(() => {
    setIsLoading(true);
    // const sanitizedDIT = cleanDITFilters(DITFilters);
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      requestId: "",
      page: paginationParams.current,
      limit: paginationParams.pageSize,
      search: policySearch || "",
      // DITFilters: sanitizedDIT,
      ...(Object.keys(appliedFilters).length > 0 && {
        filters: Object.entries(appliedFilters).map(([key, value]) => ({
          filterParameter: key,
          filterValue: value,
        })),
      }),
    };

    getPolicyList({ payload, onSuccess: handlePolicyListSuccess, onFailure: handlePolicyListFailure });
  }, [handlePolicyListSuccess, handlePolicyListFailure, appliedFilters, policySearch, paginationParams]);

  // lifeCycle hooks
  useEffect(() => {
    fetchPolicyList();
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete) return { ...item, handleOnChange: handleChangeAutocomplete, handleOnClear };
        return item;
      });
    });
  }, [fetchPolicyList]);

  useEffect(() => {
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isSelect && item?.inputName === "status") return { ...item, inputValue: selectedStatus };
        if (item?.isSelect && item?.inputName === "generatedBy") return { ...item, inputValue: selectedGeneratedBy };
        if (item?.isSelect && item?.inputName === "policyType") return { ...item, inputValue: selectedPolicyType };
        return item;
      });
    });
  }, [selectedStatus, selectedGeneratedBy, selectedPolicyType, debouncedPolicySearch]);

  useEffect(() => {
    if (location.state?.policyType) {
      applyFilter("policyType", location.state?.policyType);
      setSelectedPolicyType(location.state?.policyType);
    }
  }, [location]);

  // Methods and handlers
  const handleChangeAutocomplete = (value, name) => {
    setPolicySearch(value);
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete && item?.inputName === name) return { ...item, inputValue: value };
        return item;
      });
    });
  };

  const handleOnClear = () => {
    setPolicySearch("");
  };

  function clearFilter(type) {
    if (type === "policyType") {
      navigate(location.pathname, { replace: true, state: null });
    }
    setAppliedFilters((prev) => {
      const { [type]: _, ...rest } = prev;
      return rest;
    });
  }

  function applyFilter(type, value) {
    if (isEmpty(value)) clearFilter(type);
    else {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        [type]: value,
      }));
    }
    setPaginationParams({ page: 0, pageSize: PAGESIZE });
  }

  return (
    <>
      {contextHolder}
      <AppContentLayout
        title={<PageSection screenName="Policies" screenDescription="Access and review all company policies and compliance documents" />}
        cardChildren={
          <div className="p-4">
            <FilterComponents uiStructure={filterstate} />
            <DataGridTable
              tableStructure={tableStructure}
              data={policyData}
              loading={isLoading}
              totalCount={totalCount}
              pagination={paginationParams}
              handleChangePagination={({ page, pageSize }) => {
                setPaginationParams({ page, pageSize });
              }}
            />
          </div>
        }
      />
      {/* Drawer for conditional access JSON */}
      <Drawer
        title={
          policyJsonData?.dit?.behaviour?.disable_logon || policyJsonData?.dit?.behaviour?.disable_logon2 ? (
            <Badge.Ribbon rootClassName="!w-fit" className="!absolute !top-0" text={<span className="text-xs"> {`${policyJsonData?.dit?.behaviour?.disable_logon ? "Granted" : "Denied"}`} </span>} color={`${policyJsonData?.dit?.behaviour?.disable_logon ? "green" : "red"}`}>
              <Card className="flex !w-48 !border-0" classNames={{ body: "!p-1" }}>
                <span>Conditional Access</span>
              </Card>
            </Badge.Ribbon>
          ) : (
            "Conditional Access"
          )
        }
        onClose={() => setViewPolicyDrawer(false)}
        open={viewPolicyDrawer}
        width={450}
      >
        <DitPolicyViewer data={policyJsonData} />
      </Drawer>
    </>
  );
}

export default PolicyPage;
